// src/services/api.js
import axios from 'axios';

const baseURL = process.env.VUE_APP_API_URL;
console.log('API Base URL:', baseURL); // For debugging

// API Logging Configuration
const API_LOGGING_ENABLED = process.env.VUE_APP_API_LOGGING_ENABLED !== 'false'; // Default to true
const LOG_STYLE = {
  request: 'color: #0066cc; font-weight: bold;',
  response: 'color: #00aa00; font-weight: bold;',
  error: 'color: #cc0000; font-weight: bold;',
  header: 'color: #666666;',
  body: 'color: #333333;'
};

// Helper function to format headers for logging
const formatHeaders = (headers) => {
  if (!headers) return {};
  const formatted = {};
  Object.keys(headers).forEach(key => {
    // Skip sensitive headers in logs
    if (key.toLowerCase() !== 'authorization') {
      formatted[key] = headers[key];
    } else {
      formatted[key] = '***REDACTED***';
    }
  });
  return formatted;
};

// Helper function to log API details
const logApiDetails = (type, config, response = null) => {
  if (!API_LOGGING_ENABLED) return;

  const timestamp = new Date().toISOString();
  const groupLabel = type === 'request' 
    ? `🔵 API Request: ${config.method?.toUpperCase()} ${config.url}`
    : type === 'response'
    ? `🟢 API Response: ${config.method?.toUpperCase()} ${config.url}`
    : `🔴 API Error: ${config.method?.toUpperCase()} ${config.url}`;

  console.group(`%c${groupLabel}`, type === 'error' ? LOG_STYLE.error : (type === 'request' ? LOG_STYLE.request : LOG_STYLE.response));
  console.log(`%cTimestamp: ${timestamp}`, LOG_STYLE.header);
  
  if (type === 'request') {
    // Log request details
    console.log(`%cFull URL: ${config.baseURL}${config.url}`, LOG_STYLE.header);
    console.log(`%cMethod: ${config.method?.toUpperCase()}`, LOG_STYLE.header);
    console.log('%cRequest Headers:', LOG_STYLE.header, formatHeaders(config.headers));
    
    if (config.params) {
      console.log('%cQuery Parameters:', LOG_STYLE.header, config.params);
    }
    
    if (config.data) {
      console.log('%cRequest Body:', LOG_STYLE.header, config.data);
    }
  } else if (response) {
    // Log response details
    console.log(`%cStatus: ${response.status} ${response.statusText}`, LOG_STYLE.header);
    console.log('%cResponse Headers:', LOG_STYLE.header, formatHeaders(response.headers));
    console.log('%cResponse Data:', LOG_STYLE.header, response.data);
    
    if (response.config) {
      const duration = response.config.metadata?.endTime - response.config.metadata?.startTime;
      if (duration) {
        console.log(`%cDuration: ${duration}ms`, LOG_STYLE.header);
      }
    }
  }
  
  console.groupEnd();
};

// Create API client instance
const apiClient = axios.create({
  baseURL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 100000,
});

// For file uploads, we'll use a longer timeout
export const fileUploadClient = axios.create({
  baseURL,
  timeout: 300000, // 5 minutes for file uploads
});

// Request interceptor with logging
const requestInterceptor = (config) => {
  // Add timing metadata
  config.metadata = { startTime: Date.now() };
  
  // Add auth token
  const token = localStorage.getItem('jwt');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Log request details
  logApiDetails('request', config);
  
  return config;
};

// Request error interceptor
const requestErrorInterceptor = (error) => {
  console.error('%cAPI Request Error:', LOG_STYLE.error, error);
  return Promise.reject(error);
};

// Response interceptor with logging
const responseInterceptor = (response) => {
  // Add end time for duration calculation
  if (response.config.metadata) {
    response.config.metadata.endTime = Date.now();
  }
  
  // Log response details
  logApiDetails('response', response.config, response);
  
  return response;
};

// Response error interceptor with logging
const responseErrorInterceptor = (error) => {
  // Add end time for duration calculation
  if (error.config?.metadata) {
    error.config.metadata.endTime = Date.now();
  }
  
  // Log error details
  if (error.response) {
    logApiDetails('error', error.config, error.response);
  } else if (error.request) {
    console.group('%cAPI Request Failed (No Response)', LOG_STYLE.error);
    console.log('Request:', error.request);
    console.log('Error:', error.message);
    console.groupEnd();
  } else {
    console.error('%cAPI Error:', LOG_STYLE.error, error.message);
  }
  
  // Handle 401 unauthorized
  if (error.response?.status === 401) {
    localStorage.removeItem('jwt');
    localStorage.removeItem('userdata');
    window.location.href = '/login';
  }
  
  return Promise.reject(error);
};

// Apply interceptors to apiClient
apiClient.interceptors.request.use(requestInterceptor, requestErrorInterceptor);
apiClient.interceptors.response.use(responseInterceptor, responseErrorInterceptor);

// Apply interceptors to fileUploadClient
fileUploadClient.interceptors.request.use(requestInterceptor, requestErrorInterceptor);
fileUploadClient.interceptors.response.use(responseInterceptor, responseErrorInterceptor);

// Export additional utilities for manual logging if needed
export const apiLogger = {
  logRequest: (config) => logApiDetails('request', config),
  logResponse: (config, response) => logApiDetails('response', config, response),
  logError: (config, response) => logApiDetails('error', config, response),
  setLoggingEnabled: (enabled) => {
    window.VUE_APP_API_LOGGING_ENABLED = enabled;
  }
};

export default apiClient;